// src/products/dataroom/components/DataroomToolbar.js
// Extracted toolbar functionality from DataroomExplorer
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom'; 
import { 
  ChevronRight, Search, Upload, FolderPlus, Plus, ExternalLink, 
  Merge, MessageSquare, 
  Settings, ArrowUpCircle, Bookmark, Palette, Download
} from 'lucide-react';

const DataroomToolbar = ({
  breadcrumb = [],
  searchTerm,
  userRole,
  projectId,
  projectName,
  // Event handlers
  onNavigateToFolder,
  onSetSearchTerm,
  onOpenModal,
  onCreateQuestion,
  onDownloadIndex
}) => {

  const navigate = useNavigate();
  useLocation(); 
  const [showManageDropdown, setShowManageDropdown] = useState(false);

  const currentFolder = (breadcrumb && breadcrumb.length > 0) ? breadcrumb[breadcrumb.length - 1] : { id: null, name: projectName || 'Dataroom' };
  const parentFolder = (breadcrumb && breadcrumb.length > 1) ? breadcrumb[breadcrumb.length - 2] : { id: null, name: projectName || 'Dataroom' };

  const handleAskQuestion = () => {
    if (onCreateQuestion) {
      // Pass the current folder context
      onCreateQuestion({
        moduleType: 'dataroom',
        contextId: currentFolder.id || projectId, // If currentFolder is null (root), use projectId
        contextName: currentFolder.name,
        moduleReferenceId: currentFolder.id || projectId,
        moduleReferenceName: currentFolder.name
      });
    }
  };

  const isCreator = userRole === 'creator' || userRole === 'support';
  
  return (
    <>
            {/* Project Title - Always visible when project is selected */}
            {projectName && (
              <div className="bg-white border-b border-gray-200 px-4 py-3">
                <h1 className="text-xl font-bold text-gray-900">{projectName}</h1>
              </div>
            )}
            
            {/* Breadcrumb Navigation */}
            <div className="bg-white border-b border-gray-200 px-4 py-2">
              <nav className="flex items-center space-x-2 text-sm">
                {/* Folder Breadcrumb Path */}
                {breadcrumb && breadcrumb.length > 0 && breadcrumb.map((item, index) => (
                  <React.Fragment key={item.id || index}>
                    {index > 0 && <ChevronRight className="h-4 w-4 text-gray-400" />}
                    <button
                      onClick={() => onNavigateToFolder && onNavigateToFolder(item.id)}
                      className={`text-gray-600 hover:text-blue-600 transition-colors ${
                        index === breadcrumb.length - 1 ? 'font-medium text-gray-900' : ''
                      }`}
                    >
                      {item.name}
                    </button>
                  </React.Fragment>
                ))}
              </nav>
            </div>
            
            {/* Secondary Toolbar Area (Actions - Split Creator/Viewer) */}
            <div className="flex items-center justify-between p-4 border-b bg-gray-50">
              
              {/* Left Side: Quick Actions + Search */}
              <div className="flex items-center space-x-2">
                  
                  {/* Up (a level) */}
                  <button 
                      onClick={() => onNavigateToFolder && onNavigateToFolder(parentFolder.id)}
                      disabled={!parentFolder || parentFolder.id === null || (breadcrumb && breadcrumb.length <= 1)}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed h-10"
                      title={breadcrumb && breadcrumb.length > 1 ? `Go up to ${parentFolder.name}` : "Already at root level"}
                  >
                      <ArrowUpCircle className="h-5 w-5" />
                  </button>
                  
                  {/* Ask a Question */}
                  <button 
                      onClick={handleAskQuestion}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium text-orange-700 bg-orange-100 border border-orange-200 rounded-lg hover:bg-orange-200 transition-colors h-10"
                      title="Ask a question about this project"
                  >
                      <MessageSquare className="h-5 w-5" />
                  </button>
                  
                  {/* Bookmark */}
                  <button 
                      onClick={() => onOpenModal('bookmark', { 
                        itemId: currentFolder.id || projectId, 
                        itemName: currentFolder.name || `${projectName || 'Dataroom'} Home` 
                      })}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 h-10"
                      title="Bookmark this folder"
                  >
                      <Bookmark className="h-5 w-5" />
                  </button>
      
                  {/* Download Index Button */}
                  <button 
                      onClick={onDownloadIndex}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 h-10"
                      title="Download Index"
                  >
                      <Download className="h-5 w-5" />
                  </button>

                  {/* Search - Moved here */}
                  <div className="relative ml-4">
                      <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search files and folders..."
                        value={searchTerm}
                        onChange={(e) => onSetSearchTerm(e.target.value)}
                        className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-64 h-10"
                      />
                  </div>
              </div>
      
              {/* Right Side: Management Actions */}
              <div className="flex items-center space-x-2">
                  
                  {/* Action Buttons (New Folder, Upload, Templates) */}
                  <button 
                      onClick={() => onOpenModal('createFolder')}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 h-10"
                      title="New Folder"
                  >
                      <FolderPlus className="h-5 w-5" />
                  </button>
                  
                  <button 
                      onClick={() => onOpenModal('upload')}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 h-10"
                      title="Upload File"
                  >
                      <Upload className="h-5 w-5" />
                  </button>
                  
                  <button 
                      onClick={() => onOpenModal('template')}
                      className="inline-flex items-center px-3 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 h-10"
                      title="Apply Template"
                  >
                      <Plus className="h-5 w-5" />
                  </button>

                  {/* Admin Controls (Settings/Manage) */}
                  {isCreator && (
                      <div className="flex items-center space-x-2 border-l pl-4 ml-4 border-gray-200">
                          
                          {/* Settings Button */}
                          {projectId && (
                            <button
                                onClick={() => navigate(`/dataroom/${projectId}/settings`)}
                                className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg text-sm font-medium hover:bg-gray-700 h-10"
                                title="Project Settings"
                            >
                                <Settings className="h-5 w-5" />
                            </button>
                          )}

                          {/* Manage Dropdown Button */}
                          <div className="relative">
                              <button
                                  onClick={() => setShowManageDropdown(!showManageDropdown)}
                                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 h-10"
                              >
                                  <Settings className="h-5 w-5 mr-2" />
                                  Manage
                              </button>
      
                              {showManageDropdown && (
                                  <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-10" onClick={() => setShowManageDropdown(false)}>
                                      <button
                                          onClick={() => onOpenModal('projectColor', { id: projectId, project_name: projectName })}
                                          className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                      >
                                          <Palette className="h-4 w-4 mr-2" />
                                          Project Color
                                      </button>
                                      <button
                                          onClick={() => navigate(`/dataroom/${projectId}/settings`)}
                                          className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                      >
                                          <Settings className="h-4 w-4 mr-2" />
                                          Project Details
                                      </button>
                                      <div className="border-t border-gray-100 my-1"></div>
                                      <button
                                          onClick={() => onOpenModal('embed')}
                                          className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                      >
                                          <ExternalLink className="h-4 w-4 mr-2" />
                                          Embed Dataroom
                                      </button>
                                      <button
                                          onClick={() => onOpenModal('merge')}
                                          className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                      >
                                          <Merge className="h-4 w-4 mr-2" />
                                          Merge Dataroom
                                      </button>
                                  </div>
                              )}
                          </div>
                      </div>
                  )}
              </div>
            </div>

    </>
  );
};

export default React.memo(DataroomToolbar);